import { IDomainDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import * as React from "react";
import { DomainService } from "../../../../services/domain-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import "./create-domain-modal.scss";

/** The `id` the footer's submit button targets — see `KModalFooter.submitForm`. */
const FORM_ID: string = "create-workspace-form";

/**
 * The managed database a new workspace is created on. The values are the API's, so they
 * stay numbers rather than becoming a string union.
 */
const DB_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: 1, label: "SQL Server" },
  { value: 2, label: "Oracle" },
  { value: 3, label: "PostgreSQL" }
];

/** What the commit is doing, rather than the generic "Saving". */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

export interface ICreateDomainModalState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

export interface ICreateDomainModalProps {
  closeModal: () => void;
  selectDomain?: (domainName: string) => void;
}

export interface ICreateDomainFormValues {
  name: string;
  managedDbType: number;
}

class CreateDomainModal extends React.PureComponent<
  ICreateDomainModalProps & IWithContextProp,
  ICreateDomainModalState
> {
  private formRef = React.createRef<FormInstance>();

  private inputRef = React.createRef<HTMLInputElement>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateDomainModalProps & IWithContextProp) {
    super(props);
    this.state = {
      saveStatus: "idle"
    };
  }

  componentDidMount() {
    // The name is the only thing this dialog opens for, so it takes the caret. The delay
    // is the dialog's own mount animation — focusing before it settles loses the caret.
    setTimeout((): void => {
      this.inputRef.current?.focus();
    }, 100);
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  /**
   * Cancel, the X and Escape. The parent unmounts this dialog on close, which would dispose the
   * success timer — and with it the switch into the new workspace — so a close during the hold waits.
   */
  handleClose = (): void => {
    // Closing during the ✓ hold would cancel the close the success already scheduled (and with it
    // the reload/refresh that follows), so it is swallowed; the dialog leaves on its own a moment later.
    if (this.saveStatus.current === "success") return;
    this.props.closeModal();
  };

  onSubmit = async (data: ICreateDomainFormValues): Promise<void> => {
    // A second submit while the commit runs or its ✓ is held (the button is live again during the
    // hold, and Enter submits the form regardless) would call the service twice and cancel the close.
    if (this.saveStatus.current === "loading" || this.saveStatus.current === "success") return;
    this.saveStatus.start();
    try {
      const obj = {
        name: KMainFunctions.turkishCharsToEnglish(data.name),
        managedDbType: data.managedDbType
      } as IDomainDto;
      const res = await DomainService.Create(obj);
      if (res.status === 200) {
        // No toast: the ✓ says it was created, and the switcher then moves to the new workspace,
        // which is the result on screen. The dialog leaves after its ✓ — see `SaveStatus`.
        this.saveStatus.succeed((): void => {
          this.props.closeModal();
          if (this.props.selectDomain) {
            this.props.selectDomain(obj.name);
          }
        });
        return;
      }
      this.saveStatus.fail();
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
      this.saveStatus.fail();
    }
  };

  render() {
    const footer = (
      <KModalFooter
        onCancel={this.handleClose}
        cancelTestId="cancelButton"
        submitLabel="Create"
        submitTestId="createWorkspaceButton"
        submitForm={FORM_ID}
        status={this.state.saveStatus}
        statusLabels={CREATE_STATUS_LABELS}
      />
    );

    return (
      <KModal
        showModal
        closeModal={this.handleClose}
        header="Create Workspace"
        footer={footer}
        width={460}
        centered
        isLoading={this.state.saveStatus === "loading"}
      >
        <div className="kuika-create-workspace-modal">
          <div className="kuika-create-workspace-modal__header">
            <KuikaModalIconWrapper iconName="app" iconType="sidebar-icons" intent="create" />
            <div className="kuika-create-workspace-modal__title">
              <KuikaH5>Create Workspace</KuikaH5>
              <KuikaText className="kuika-create-workspace-modal__subtitle">
                Give the workspace a name and pick the database it runs on
              </KuikaText>
            </div>
          </div>

          <Form
            id={FORM_ID}
            ref={this.formRef}
            name="createWorkspace"
            onFinish={this.onSubmit}
            initialValues={{ name: undefined, managedDbType: 1 }}
          >
            <div className="kuika-create-workspace-modal__content">
              <div className="kuika-create-workspace-modal__form-item">
                <KuikaSmallText className="kuika-create-workspace-modal__label">Name</KuikaSmallText>
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a workspace name!" },
                    { max: 30, message: "Workspace name should contains up to 30 characters!" },
                    {
                      pattern: /^[A-Za-z_][A-Za-z0-9_]*$/,
                      message:
                        "Workspace name must contains A-Z or a-z or 0-9 or lower dash (_) and should not start with numbers!"
                    }
                  ]}
                >
                  <KuikaInput
                    data-testid="workspaceNameInput"
                    ref={this.inputRef}
                    placeholder="Enter workspace name"
                    size="md"
                  />
                </Form.Item>
              </div>

              <div className="kuika-create-workspace-modal__form-item">
                <KuikaSmallText className="kuika-create-workspace-modal__label">Database Type</KuikaSmallText>
                <Form.Item name="managedDbType" rules={[{ required: true, message: "Please choose a database type!" }]}>
                  <KuikaSelectbox
                    data-testid="workspaceDatabaseTypeSelect"
                    placeholder="Choose Database Type"
                    options={DB_TYPE_OPTIONS}
                    size="md"
                  />
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>
      </KModal>
    );
  }
}

const createDomainModal = withContext<ICreateDomainModalProps>(CreateDomainModal);
export { createDomainModal as CreateDomainModal };
